import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import org.example.zone360.components.CustomSpacer
import org.example.zone360.componentss.FieldTitleTextNormalSimple
import org.example.zone360.theme.Palette
import org.example.zone360.utils.getCurrentMilliseconds
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.HTMLCanvasElement

@Composable
fun SpiderChartForDashboard(
    chartId: String,
    labels: List<String>,
    values: List<Int>,
    color: String,
    score: Int = 0,
    clubAvg: Int = 0,
    chartHeight: Int = 180,
    showScore: Boolean = true,
    isCoachPage: Boolean = false,
    sessionBorder: Boolean = false,
    sessionBorderWidth: Int = 220,
    fillOpacity: Double = 0.3,
    maxValue: Int = 100,
    showSelection: Boolean = false,
    marginRight: Int = 0
) {
    var isDataReady by remember { mutableStateOf(false) }
    val chartRendered = remember { mutableStateOf(false) }
    var retryCount by remember { mutableStateOf(0) }

    val uniqueChartId = remember(chartId) {
        "${chartId}_${getCurrentMilliseconds()}"
    }

    // ✅ Canvas dimensions
    val canvasHeight = if (sessionBorder) 150 else chartHeight

    Column(
        modifier = Modifier
            .fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Box(
            modifier = Modifier
                .then(
                    if (sessionBorder) Modifier.width(240.px)
                        .margin(right = marginRight.px)
                    else Modifier.fillMaxWidth()
                )
                .height(canvasHeight.px),
            contentAlignment = Alignment.Center
        ) {
            if (sessionBorder) {
                Canvas(
                    Modifier
                        .width(200.px)
//                        .height(0.px)
                        .id(uniqueChartId)
                        .toAttrs()
                ) {}
            } else {
                Canvas(
                    Modifier
                        .fillMaxWidth()
                        .height(canvasHeight.px)
                        .id(uniqueChartId)
                        .toAttrs()
                ) {}
            }
        }
        CustomSpacer(height = if (sessionBorder) 15.px else 0.px)
        if (showScore) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(
                        when {
                            isCoachPage && showSelection -> 40.px
                            showSelection || isCoachPage -> 55.px
                            else -> 60.px
                        }
                    )
                    .padding(leftRight = 5.px)
                    .margin(top = if (showSelection) 0.px else 14.px)
            ) {
                Row(
                    verticalAlignment = Alignment.Bottom,
                    modifier = Modifier.weight(1)
                ) {
                    FieldTitleTextNormalSimple(
                        text = "$score",
                        fontSize = 24.px,
                        fontWeight = 700
                    )
                    FieldTitleTextNormalSimple(
                        text = "/$maxValue",
                        fontSize = 13.px,
                        fontWeight = 400,
                        color = Palette.CoachTalkTableHeaderText.rgbLight,
                        marginLeft = 2.px
                    )
                }

                val avgLabel = when {
                    sessionBorder -> "Avg"
                    isCoachPage -> "Coach Avg"
                    else -> "Club Avg"
                }
                Box(
                    modifier = Modifier
                        .backgroundColor(Palette.WidgetAverageBackground.rgbLight)
                        .borderRadius(99.px)
                        .padding(leftRight = 10.px, topBottom = 4.px),
                    contentAlignment = Alignment.Center
                ) {
                    FieldTitleTextNormalSimple(
                        text = "$avgLabel: $clubAvg",
                        fontSize = 11.px,
                        fontWeight = 500,
                        color =  Palette.WidgetAverageText.rgbLight
                    )
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        if (!isDataReady) isDataReady = true
    }

    LaunchedEffect(isDataReady, retryCount, labels, values) {
        if (!isDataReady) return@LaunchedEffect

        val chartExists = js("typeof Chart !== 'undefined'") as Boolean
        if (!chartExists) {
            if (retryCount < 10) {
                console.warn("Chart.js not loaded, retry ${retryCount + 1}/10")
                kotlinx.coroutines.delay(300)
                retryCount++
            }
            return@LaunchedEffect
        }

        val canvas = document.getElementById(uniqueChartId) as? HTMLCanvasElement
        if (canvas == null) {
            console.warn("Canvas element with id '$chartId' not found")
            return@LaunchedEffect
        }

        val script = """
            (function() {
                try {
                    if (typeof Chart === 'undefined') {
                        console.error('Chart.js not loaded');
                        return;
                    }
                    
                    const canvas = document.getElementById('$uniqueChartId');
                    if (!canvas) {
                        console.error('Canvas not found');
                        return;
                    }
                    
                    const existingChart = Chart.getChart(canvas);
                    if (existingChart) {
                        existingChart.destroy();
                    }
                    
                    new Chart(canvas, {
                        type: 'radar',
                        data: {
                            labels: [${labels.joinToString(",") { "\"$it\"" }}],
                            datasets: [{
                                data: [${values.joinToString(",")}],
                                fill: true,
                                backgroundColor: '${hexToRgba(color, fillOpacity)}',
                                borderColor: '$color',
                                borderWidth: 2,
                                pointRadius: 4,
                                pointHoverRadius: 6,
                                pointBackgroundColor: '$color',
                                pointBorderColor: '#FFFFFF',
                                pointBorderWidth: 2,
                                tension: 0.1
                            }]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: false },
                                tooltip: {
                                    enabled: true,
                                    callbacks: {
                                        label: function(context) {
                                            return context.raw + '/$maxValue';
                                        }
                                    }
                                }
                            },
                            scales: {
                                r: {
                                    min: 0,
                                    max: $maxValue,
                                    beginAtZero: true,
                                    ticks: {
                                        display: false,
                                        stepSize: 20,
                                        backdropColor: 'transparent'
                                    },
                                    grid: {
                                        color: 'rgba(229, 231, 235, 0.5)',
                                        circular: true
                                    },
                                    angleLines: {
                                        color: 'rgba(209, 213, 219, 0.5)'
                                    },
                                    pointLabels: {
                                        display: true,
                                        color: '#374151',
                                        font: {
                                            size: 10,
                                            weight: '600'
                                        },
                                        padding: 4
                                    }
                                }
                            },
                            elements: {
                                line: { tension: 0.1 }
                            }
                        }
                    });
                    console.log('Chart created successfully for $uniqueChartId');
                } catch (e) {
                    console.error('Error creating chart:', e);
                }
            })();
        """.trimIndent()

        try {
            val existingScript = document.getElementById("radarChartScript_$uniqueChartId")
            existingScript?.remove()

            val scriptElement = document.createElement("script")
            scriptElement.textContent = script
            scriptElement.setAttribute("id", "radarChartScript_$uniqueChartId")
            document.body?.appendChild(scriptElement)

            chartRendered.value = true
            console.log("Chart script executed for $uniqueChartId")
        } catch (e: dynamic) {
            console.error("Failed to create chart:", e)
        }
    }
}
//@Composable
//fun SpiderChartForDashboard(
//    chartId: String,
//    labels: List<String>,
//    values: List<Int>,
//    color: String,
//    score: Int = 0,
//    clubAvg: Int = 0,
//    chartHeight: Int = 180,
//    showScore: Boolean = true,
//    isCoachPage: Boolean = false,
//    sessionBorder: Boolean = false,
//    fillOpacity: Double = 0.3,
//    maxValue: Int = 100,
//    showSelection: Boolean = false
//) {
//    var isDataReady by remember { mutableStateOf(false) }
//    val chartRendered = remember { mutableStateOf(false) }
//    var retryCount by remember { mutableStateOf(0) }
//
//    val uniqueChartId = remember(chartId) {
//        "${chartId}_${getCurrentMilliseconds()}"
//    }
//
//    Column(
//        modifier = Modifier
//            .fillMaxWidth()
//            .padding(left = 4.px)
//    ) {
//        // Spider Chart
//        Box(
//            modifier = Modifier
//                .fillMaxWidth()
//                .height(chartHeight.px)
//        ) {
//            if (sessionBorder) {
//                Canvas(
//                    Modifier
//                        .width(170.px)
//                        .height(170.px)
//                        .id(uniqueChartId)
//                        .toAttrs()
//                ) {}
//            } else {
//                Canvas(
//                    Modifier
//                        .fillMaxWidth()
//                        .height(chartHeight.px)
//                        .id(uniqueChartId)
//                        .toAttrs()
//                ) {}
//            }
////            Canvas(
////                Modifier
////                    .fillMaxWidth()
////                    .height(chartHeight.px)
////                    .id(uniqueChartId)
////                    .toAttrs()
////            ) {}
//        }
//
//        // Score section below the chart
//        if (showScore) {
//
//            Row(
//                verticalAlignment = Alignment.CenterVertically,
//                modifier = Modifier.fillMaxWidth()
//                    .height(
//                        when {
//                            isCoachPage && showSelection -> 40.px
//                            showSelection || isCoachPage -> 55.px
//                            else -> 60.px
//                        }
//                    )
//                    .padding(leftRight = 5.px).margin(top = if (showSelection) 0.px else 14.px)
//            ) {
//                Row(
//                    verticalAlignment = Alignment.Bottom,
//                    modifier = Modifier.weight(1)
//                ) {
//                    FieldTitleTextNormalSimple(
//                        text = "$score",
//                        fontSize = 24.px,
//                        fontWeight = 700
//                    )
//                    FieldTitleTextNormalSimple(
//                        text = "/$maxValue",
//                        fontSize = 13.px,
//                        fontWeight = 400,
//                        color = Palette.CoachTalkTableHeaderText.rgbLight,
//                        marginLeft = 2.px
//                    )
//                }
//                Box(
//                    modifier = Modifier
//                        .backgroundColor(Palette.WidgetAverageBackground.rgbLight)
//                        .borderRadius(99.px)
//                        .padding(leftRight = 10.px, topBottom = 4.px),
//                    contentAlignment = Alignment.Center
//                ) {
//                    FieldTitleTextNormalSimple(
//                        text = "Club Avg: $clubAvg",
//                        fontSize = 11.px,
//                        fontWeight = 500,
//                        color = Palette.WidgetAverageText.rgbLight
//                    )
//                }
//            }
//        }
//    }
//
//    // Initialize data
//    LaunchedEffect(Unit) {
//        if (!isDataReady) {
//            isDataReady = true
//        }
//    }
//
//    // Render chart with retry mechanism
//    LaunchedEffect(isDataReady, retryCount, labels, values) {
//        if (!isDataReady) return@LaunchedEffect
//
//        // Check if Chart.js is loaded
//        val chartExists = js("typeof Chart !== 'undefined'") as Boolean
//        if (!chartExists) {
//            if (retryCount < 10) {
//                console.warn("Chart.js not loaded, retry ${retryCount + 1}/10")
//                kotlinx.coroutines.delay(300)
//                retryCount++
//            }
//            return@LaunchedEffect
//        }
//
//        val canvas = document.getElementById(uniqueChartId) as? HTMLCanvasElement
//        if (canvas == null) {
//            console.warn("Canvas element with id '$chartId' not found")
//            return@LaunchedEffect
//        }
//
////        if (chartRendered.value) return@LaunchedEffect
//
//        // Build the script
//        val script = """
//            (function() {
//                try {
//                    // Check if Chart.js is loaded
//                    if (typeof Chart === 'undefined') {
//                        console.error('Chart.js not loaded');
//                        return;
//                    }
//
//                    const canvas = document.getElementById('$uniqueChartId');
//                    if (!canvas) {
//                        console.error('Canvas not found');
//                        return;
//                    }
//
//                    // Destroy existing chart
//                    const existingChart = Chart.getChart(canvas);
//                    if (existingChart) {
//                        existingChart.destroy();
//                    }
//
//                    // Create new chart with single dataset and fill color
//                    new Chart(canvas, {
//                        type: 'radar',
//                        data: {
//                            labels: [${labels.joinToString(",") { "\"$it\"" }}],
//                            datasets: [{
//                                data: [${values.joinToString(",")}],
//                                fill: true,
//                                backgroundColor: '${hexToRgba(color, fillOpacity)}',
//                                borderColor: '$color',
//                                borderWidth: 2,
//                                pointRadius: 4,
//                                pointHoverRadius: 6,
//                                pointBackgroundColor: '$color',
//                                pointBorderColor: '#FFFFFF',
//                                pointBorderWidth: 2,
//                                tension: 0.1
//                            }]
//                        },
//                        options: {
//                        responsive: ${!sessionBorder},
//                        maintainAspectRatio: ${sessionBorder},
//                        aspectRatio: ${if (sessionBorder) 1 else 2},
//                            plugins: {
//                                legend: {
//                                    display: false
//                                },
//                                tooltip: {
//                                    enabled: true,
//                                    callbacks: {
//                                        label: function(context) {
//                                            return context.raw + '/$maxValue';
//                                        }
//                                    }
//                                }
//                            },
//                            scales: {
//                                r: {
//                                    min: 0,
//                                    max: $maxValue,
//                                    beginAtZero: true,
//                                    ticks: {
//                                        display: false,
//                                        stepSize: 20,
//                                        backdropColor: 'transparent'
//                                    },
//                                    grid: {
//                                        color: 'rgba(229, 231, 235, 0.5)',
//                                        circular: true
//                                    },
//                                    angleLines: {
//                                        color: 'rgba(209, 213, 219, 0.5)'
//                                    },
//                                    pointLabels: {
//                                        display: true,
//                                        color: '#374151',
//                                        font: {
//                                            size: 10,
//                                            weight: '600'
//                                        },
//                                        padding: 4
//                                    }
//                                }
//                            },
//                            elements: {
//                                line: {
//                                    tension: 0.1
//                                }
//                            }
//                        }
//                    });
//                    console.log('Chart created successfully for $uniqueChartId');
//                } catch (e) {
//                    console.error('Error creating chart:', e);
//                }
//            })();
//        """.trimIndent()
//
//        try {
//            val scriptElement = document.createElement("script")
//            scriptElement.textContent = script
//            scriptElement.setAttribute("id", "radarChartScript_$uniqueChartId")
//
//            val existingScript = document.getElementById("radarChartScript_$uniqueChartId")
//            if (existingScript != null) {
//                existingScript.remove()
//            }
//
//            document.body?.appendChild(scriptElement)
//            chartRendered.value = true
//            console.log("Chart script executed for $uniqueChartId")
//        } catch (e: dynamic) {
//            console.error("Failed to create chart:", e)
//        }
//    }
//}

fun hexToRgba(hex: String, alpha: Double): String {
    val clean = hex.trimStart('#')
    val r = clean.substring(0, 2).toInt(16)
    val g = clean.substring(2, 4).toInt(16)
    val b = clean.substring(4, 6).toInt(16)
    return "rgba($r,$g,$b,$alpha)"
}